import React, { FC, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnalysisSectionHeader,
  ValuationComparisonChart,
  ValuationChartData,
  Icon,
} from '@popety-io/ui'
import {
  EstimateLogValuation,
  MultiProviderValuation,
  Valuation,
} from '@popety-io/types'
import { useAutoValuationData } from '../Valuations/hooks/useAutoValuationData'
import { getProviderInfo } from './utils/valuationColors'
import { ValuationCard, AutoValuationDetail } from './components'
import { useClearMapSection } from '../../hooks'
import { usePublicPricePerSqm } from '../../contexts'
import {
  ContentWrapper,
  CardGrid,
  ChartContainer,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
} from './AutoValuationsTab.styles'

type ViewState = 'list' | 'detail'

interface AutoValuationsTabProps {
  estimateData: any
}

export const AutoValuationsTab: FC<AutoValuationsTabProps> = ({
  estimateData,
}) => {
  const { t } = useTranslation('estimate')

  // Clear map section data when viewing auto valuations
  useClearMapSection()

  // Price-per-m² context (session-only, provided at page level)
  const { show, areas, resolveArea } = usePublicPricePerSqm()
  const basisArea = resolveArea(areas)

  // Get log_valuation data from estimate
  const logValuation: EstimateLogValuation | MultiProviderValuation | null =
    estimateData?.log_valuation ?? null

  // View state management
  const [viewState, setViewState] = useState<ViewState>('list')
  const [selectedValuationId, setSelectedValuationId] = useState<string | null>(
    null
  )

  // Use custom hook to parse auto valuation data (filters out manual valuations)
  const { valuations: initialValuations } = useAutoValuationData(
    logValuation ?? null
  )

  // Get hidden valuation IDs from value_edited
  const hiddenValuationIds: string[] = useMemo(
    () => estimateData?.value_edited?.hiddenValuationIds || [],
    [estimateData?.value_edited?.hiddenValuationIds]
  )

  // Add provider color and filter out hidden valuations
  const valuations = useMemo(
    () =>
      initialValuations
        .filter((valuation) => !hiddenValuationIds.includes(valuation.id))
        .map((valuation) => ({
          ...valuation,
          isVisible: true,
          color: getProviderInfo(valuation.source).color,
          name: getProviderInfo(valuation.source).name,
        })),
    [initialValuations, hiddenValuationIds]
  )

  // Prepare chart data (all valuations visible in read-only mode)
  const chartData: ValuationChartData[] = useMemo(
    () =>
      valuations.map((valuation) => ({
        source: valuation.source,
        name: valuation.name,
        value: valuation.value,
        confidenceLow: valuation.range?.min,
        confidenceHigh: valuation.range?.max,
        color: valuation.color,
        area: basisArea,
      })),
    [valuations, basisArea]
  )

  // Navigation helpers
  const showList = useCallback(() => {
    setViewState('list')
    setSelectedValuationId(null)
  }, [])

  const showDetail = useCallback((id: string) => {
    setViewState('detail')
    setSelectedValuationId(id)
  }, [])

  // Find selected valuation for detail view
  const selectedValuation = useMemo(
    () => valuations.find((v) => v.id === selectedValuationId),
    [valuations, selectedValuationId]
  )

  // Empty state
  if (valuations.length === 0) {
    return (
      <div>
        <AnalysisSectionHeader
          title={t('autoValuations.title')}
          subtitle={t('autoValuations.subtitle')}
        />
        <ContentWrapper>
          <EmptyState>
            <EmptyStateIcon>
              <Icon name="calculator" size="xlarge" />
            </EmptyStateIcon>
            <EmptyStateTitle>{t('autoValuations.empty.title')}</EmptyStateTitle>
            <EmptyStateText>{t('autoValuations.empty.text')}</EmptyStateText>
          </EmptyState>
        </ContentWrapper>
      </div>
    )
  }

  // Detail view
  if (viewState === 'detail' && selectedValuation) {
    return (
      <div>
        <AnalysisSectionHeader
          title={t('autoValuations.title')}
          subtitle={t('autoValuations.subtitle')}
        />
        <ContentWrapper>
          <AutoValuationDetail
            valuation={selectedValuation as Valuation}
            onBack={showList}
          />
        </ContentWrapper>
      </div>
    )
  }

  // List view
  return (
    <div>
      <AnalysisSectionHeader
        title={t('autoValuations.title')}
        subtitle={t('autoValuations.subtitle')}
      />

      <ContentWrapper>
        {/* Comparison Chart */}
        <ChartContainer>
          <ValuationComparisonChart
            data={chartData}
            t={(key) => t(key.replace('valuations.', ''))}
            showPricePerSqm={show}
          />
        </ChartContainer>

        {/* Valuation Cards Grid */}
        <CardGrid>
          {valuations.map((valuation) => (
            <ValuationCard
              key={valuation.id}
              valuation={valuation as Valuation}
              onClick={() => showDetail(valuation.id)}
            />
          ))}
        </CardGrid>
      </ContentWrapper>
    </div>
  )
}
